/**
 * CurrentSubscriptions (Money → Current Subscriptions)
 * ----------------------------------------------------
 * Owner roster of everyone with an ACTIVE subscription, grouped per family.
 * Reuses the exact same SubRow cancel buttons as the profile
 * (ActiveSubscriptionsPanel) so cancelling here behaves identically.
 * Sachin 2026-06: "Name it Current Subscriptions, buttons to cancel same
 * as the profile ones."
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CreditCard, Search, Zap, RefreshCw, RefreshCwOff } from "lucide-react";
import { SubRow } from "@/components/coach/leadDetail/ActiveSubscriptionsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) =>
    cents == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

/**
 * AutoRenewRow — an auto-renewing PACKAGE (or legacy credit) shown on the
 * Subscriptions page. "Stop auto-renew" turns off the auto-charge without
 * touching the current sessions (pack keeps running to completion).
 */
function AutoRenewRow({ userId, item, onStopped }) {
    const [busy, setBusy] = useState(false);
    const isCredit = item.kind === "credit";
    const id = isCredit ? item.credit_id : item.pack_id;

    const stop = async () => {
        if (!window.confirm(
            `Stop auto-renew for "${item.label}"?\n\n` +
            "The current sessions stay usable — but when they run out, NO new package will auto-charge. " +
            "The card on file is not touched. You can turn it back on from the profile.",
        )) return;
        setBusy(true);
        try {
            const url = isCredit
                ? `${API}/coach/clients/${userId}/credits/${id}/auto-renew/toggle`
                : `${API}/coach/clients/${userId}/packs/${id}/auto-renew/toggle`;
            await axios.post(url, { enabled: false }, { withCredentials: true });
            toast.success("Auto-renew stopped", {
                description: `"${item.label}" won't auto-charge again. Current sessions are unaffected.`,
            });
            onStopped?.(id);
        } catch (e) {
            toast.error("Couldn't stop auto-renew", { description: e?.response?.data?.detail || "Unknown error" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid={`auto-renew-row-${id}`}
            className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-zinc-900"
        >
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <span className="text-sm text-white truncate">{item.label}</span>
                    <span className="text-[9px] tracking-[0.18em] uppercase bg-amber-950/40 text-amber-300 border border-amber-800/50 px-1.5 py-0.5 rounded whitespace-nowrap">
                        Auto-renew pack
                    </span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                    {item.remaining_sessions}/{item.sessions_total} left · renews at {fmtMoney(item.renewal_price_cents)} when sessions run out
                    {item.last_renewal_failed_reason && (
                        <span className="text-rose-400"> · last renewal failed: {item.last_renewal_failed_reason}</span>
                    )}
                </div>
            </div>
            <button
                type="button"
                data-testid={`auto-renew-stop-${id}`}
                onClick={stop}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase border border-zinc-700 bg-zinc-900/40 text-zinc-200 px-3 py-2 hover:border-rose-600 hover:text-rose-300 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCwOff className="w-3 h-3" />}
                Stop auto-renew
            </button>
        </div>
    );
}

export default function CurrentSubscriptions() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/current-subscriptions`, { withCredentials: true });
            setData(r.data || null);
        } catch {
            if (!silent) setData(null);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // When a row is cancelled: for an IMMEDIATE cancel, drop it from the list
    // right away so there's zero confusion (don't wait on a refetch that can
    // read stale data during DB replication). Then silently reconcile with the
    // server so the true state always wins. (Sachin 2026-06.)
    const handleChanged = useCallback((info) => {
        if (info?.removed && info?.subscriptionId) {
            setData((prev) => {
                if (!prev) return prev;
                const families = prev.families
                    .map((f) => ({
                        ...f,
                        subscriptions: (f.subscriptions || []).filter(
                            (s) => s.subscription_id !== info.subscriptionId,
                        ),
                    }))
                    .filter((f) => f.subscriptions.length > 0);
                return {
                    ...prev,
                    families,
                    family_count: families.length,
                    subscription_count: Math.max(0, (prev.subscription_count || 1) - 1),
                };
            });
        }
        // Reconcile with the server shortly after (covers write replication lag).
        setTimeout(() => load(true), 1200);
    }, [load]);

    // Stopping an auto-renew pack/credit: drop it from the family immediately,
    // then drop families that now have nothing left, and reconcile.
    const handleStopped = useCallback((uid, itemId) => {
        setData((prev) => {
            if (!prev) return prev;
            const families = prev.families
                .map((f) => f.user_id !== uid ? f : ({
                    ...f,
                    auto_renews: (f.auto_renews || []).filter(
                        (it) => it.pack_id !== itemId && it.credit_id !== itemId,
                    ),
                }))
                .filter((f) => (f.subscriptions || []).length > 0 || (f.auto_renews || []).length > 0);
            return {
                ...prev,
                families,
                family_count: families.length,
                auto_renew_count: Math.max(0, (prev.auto_renew_count || 1) - 1),
            };
        });
        setTimeout(() => load(true), 1200);
    }, [load]);

    const families = useMemo(() => {
        const list = data?.families || [];
        const term = q.trim().toLowerCase();
        if (!term) return list;
        return list.filter((f) =>
            (f.name || "").toLowerCase().includes(term) ||
            (f.email || "").toLowerCase().includes(term) ||
            (f.subscriptions || []).some((s) => (s.athlete_name || "").toLowerCase().includes(term)) ||
            (f.auto_renews || []).some((it) => (it.label || "").toLowerCase().includes(term)),
        );
    }, [data, q]);

    return (
        <div className="min-h-screen bg-black text-zinc-100 px-4 py-8 md:px-10" data-testid="current-subscriptions-page">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-end justify-between gap-3 flex-wrap mb-1">
                    <h1 className="font-display text-5xl md:text-6xl tracking-wider flex items-center gap-3">
                        <CreditCard className="w-8 h-8 text-emerald-400" />
                        CURRENT SUBSCRIPTIONS
                    </h1>
                </div>
                <p className="text-sm text-zinc-500 mb-6">
                    Everyone with an active recurring subscription <span className="text-amber-300">or an auto-renewing package</span>, grouped by family.
                    Cancel a subscription or stop a package auto-renew from here exactly like you would on a profile.
                </p>

                <div className="flex items-center gap-3 flex-wrap mb-6">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            data-testid="current-subs-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by parent, email, or athlete…"
                            className="w-full bg-zinc-950 border border-zinc-800 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-emerald-700"
                        />
                    </div>
                    {data && (
                        <span data-testid="current-subs-count" className="text-[11px] tracking-[0.25em] uppercase text-zinc-400 border border-zinc-800 px-3 py-2">
                            {data.family_count} families · {data.subscription_count} subs · {data.auto_renew_count || 0} auto-renew packs
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading subscriptions…
                    </div>
                ) : !data || families.length === 0 ? (
                    <div data-testid="current-subs-empty" className="border border-zinc-900 bg-zinc-950/40 p-10 text-center text-zinc-500">
                        {q ? "No matches for that search." : "No active subscriptions right now."}
                    </div>
                ) : (
                    <div className="space-y-5">
                        {families.map((fam) => (
                            <section
                                key={fam.user_id}
                                data-testid={`family-block-${fam.user_id}`}
                                className="border border-zinc-900 bg-zinc-950/40"
                            >
                                <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-zinc-900">
                                    <div>
                                        <Link
                                            to={`/coach/leads/${fam.user_id}`}
                                            data-testid={`family-link-${fam.user_id}`}
                                            className="font-display text-lg tracking-wide text-emerald-300 hover:text-emerald-200"
                                        >
                                            {fam.name}
                                        </Link>
                                        {fam.email && <div className="text-[11px] text-zinc-500">{fam.email}</div>}
                                    </div>
                                    <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-400 flex-wrap">
                                        {fam.subscriptions.length > 0 && (
                                            <span className="inline-flex items-center gap-1.5">
                                                <Zap className="w-3 h-3 text-emerald-400" />
                                                {fam.subscriptions.length} sub{fam.subscriptions.length === 1 ? "" : "s"}
                                            </span>
                                        )}
                                        {(fam.auto_renews || []).length > 0 && (
                                            <span className="inline-flex items-center gap-1.5 text-amber-300">
                                                <RefreshCw className="w-3 h-3 text-amber-400" />
                                                {fam.auto_renews.length} auto-renew pack{fam.auto_renews.length === 1 ? "" : "s"}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div>
                                    {fam.subscriptions.map((s) => (
                                        <SubRow
                                            key={s.subscription_id}
                                            userId={fam.user_id}
                                            sub={s}
                                            athletes={fam.athletes}
                                            onChanged={handleChanged}
                                        />
                                    ))}
                                    {(fam.auto_renews || []).map((it) => (
                                        <AutoRenewRow
                                            key={it.pack_id || it.credit_id}
                                            userId={fam.user_id}
                                            item={it}
                                            onStopped={(id) => handleStopped(fam.user_id, id)}
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
